import { useState } from 'react'
import { quotaTiers as api } from '@/api/endpoints'
import type { QuotaTier } from '@/api/types'
import { Err } from '@/components/Err'
import { NumberField } from '@/components/NumberField'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { EMPTY_TIER_DRAFT, tierBlocked, tierDraftOf, tierLimitsBody, type TierDraft } from '@/domain/quota'
import { FREE } from '@/domain/words'

// 직위를 만들고 고치고 지우는 창. 만들기·지우기는 목록(`QuotaTiers.tsx`)이, 고치기 창은 상세(`QuotaTierView.tsx`)의 `수정` 이 부른다(F-123).
// 상세는 값을 그 자리에서 고치는 길도 함께 둔다.
// 둘 다 대화 상자의 본문만이고 `<Dialog>` 는 부르는 쪽이 갖는다.
//
// **빈 칸이 자유다**: 비운 축은 본문에서 빠지고 placeholder 가 말없이 그것을 말한다. `0` 은
// 한 대도 가질 수 없다는 정반대의 뜻이라, 두 상태가 같은 칸에서 섞여 보이면 안 된다.
// 칸의 글자와 보내는 본문 사이의 왕복은 `domain/quota.ts` 다(`tierDraftOf`·`tierLimitsBody`).

/** 칸을 한 `map` 으로 그린다. 메모리와 디스크만 단위를 라벨에 싣는다. 순서는 목록의 칸과 같다. */
const FIELDS: { key: keyof TierDraft; label: string; id: string }[] = [
  { key: 'vms', label: 'VM (대)', id: 'tier-vms' },
  { key: 'cores', label: 'CPU (코어)', id: 'tier-cores' },
  { key: 'memoryGb', label: '메모리 (GB)', id: 'tier-memory' },
  { key: 'diskGb', label: '디스크 (GB)', id: 'tier-disk' },
]

function LimitFields({ draft, busy, onChange }: { draft: TierDraft; busy: boolean; onChange: (d: TierDraft) => void }) {
  return (
    <fieldset className="grid gap-2">
    <legend className="mb-1 text-[13px] font-medium">한 사람당 한도</legend>
    <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
      {FIELDS.map((f) => (
        <NumberField
          key={f.key}
          id={f.id}
          label={f.label}
          value={draft[f.key]}
          placeholder={FREE}
          busy={busy}
          onChange={(v) => onChange({ ...draft, [f.key]: v })}
        />
      ))}
    </div>
    </fieldset>
  )
}

/** 직위의 권한(F-126) — 지금은 노드 선택 하나다. 한도와 달리 숫자가 아니라 할 수 있는 일이다. */
function PermissionFields({ nodeChoice, busy, onChange }: { nodeChoice: boolean; busy: boolean; onChange: (v: boolean) => void }) {
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-1 text-[13px] font-medium">권한</legend>
      <div className="flex items-center gap-2">
        <Checkbox id="tier-form-node-choice" checked={nodeChoice} disabled={busy} onCheckedChange={(v) => onChange(v === true)} />
        <Label htmlFor="tier-form-node-choice">노드 선택</Label>
      </div>
    </fieldset>
  )
}

/**
 * 직위 고치기(F-123). 지금 값으로 채워진 생성 창과 같은 칸이고, 저장은 이름과 네 축 전체를 한 번에 보낸다 —
 * `limits` 를 실은 PATCH 는 다섯 축을 통째로 바꾸므로 비운 칸이 그 축을 자유로 만든다.
 */
export function TierEditForm({ tier, onDone, onClose }: { tier: QuotaTier; onDone: () => void; onClose: () => void }) {
  const [display, setDisplay] = useState(tier.display_name)
  const [draft, setDraft] = useState<TierDraft>(tierDraftOf(tier.limits))
  const [nodeChoice, setNodeChoice] = useState(tier.node_choice)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const ok = display.trim().length > 0
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      await api.patch(tier.id, { display_name: display.trim(), limits: tierLimitsBody(draft), node_choice: nodeChoice })
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <DialogHeader>
        <DialogTitle>직위 수정</DialogTitle>
      </DialogHeader>
      <div className="grid gap-1.5">
        <Label htmlFor="tier-name">이름</Label>
        <Input id="tier-name" value={display} maxLength={200} disabled={busy} onChange={(e) => setDisplay(e.target.value)} />
      </div>
      <LimitFields draft={draft} busy={busy} onChange={setDraft} />
      <PermissionFields nodeChoice={nodeChoice} busy={busy} onChange={setNodeChoice} />
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button type="submit" disabled={busy || !ok}>
          {busy ? '저장 중' : '저장'}
        </Button>
      </DialogFooter>
    </form>
  )
}

export function TierCreateForm({ onDone }: { onDone: () => void }) {
  const [display, setDisplay] = useState('')
  const [draft, setDraft] = useState<TierDraft>(EMPTY_TIER_DRAFT)
  const [nodeChoice, setNodeChoice] = useState(false)
  const [order, setOrder] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const ok = display.trim().length > 0
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      await api.create({
        display_name: display.trim(),
        limits: tierLimitsBody(draft),
        node_choice: nodeChoice,
        ...(order === '' ? {} : { sort_order: Number(order) }),
      })
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <DialogHeader>
        <DialogTitle>직위 생성</DialogTitle>
      </DialogHeader>
      <div className="grid gap-1.5">
        <Label htmlFor="tier-name">이름</Label>
        <Input id="tier-name" value={display} maxLength={200} disabled={busy} onChange={(e) => setDisplay(e.target.value)} />
      </div>
      <LimitFields draft={draft} busy={busy} onChange={setDraft} />
      <PermissionFields nodeChoice={nodeChoice} busy={busy} onChange={setNodeChoice} />
      <NumberField id="tier-order" label="순서" value={order} placeholder="0" busy={busy} onChange={setOrder} />
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onDone}>
          취소
        </Button>
        <Button type="submit" disabled={busy || !ok}>
          {busy ? '생성 중' : '생성'}
        </Button>
      </DialogFooter>
    </form>
  )
}

/**
 * 고른 직위들의 지우기. 대상의 목록을 보이고 묻는다 — 팀 삭제와 같은 확인이고, 하나여도 같은 모양이다.
 * 사람이 든 직위와 기본 직위는 그 줄에 막힘 문장이 붙고 보내지 않는다 — 몇 명을 어디서 옮겨야 하는지 알아야 다음 행동이 정해진다.
 * 인원과 기본 여부는 폴링이 갈아 주는 그 줄의 값이라 창을 연 채 바뀌면 창도 따라 바뀐다.
 *
 * 하나씩 지우고 거절된 직위만 남아 같은 버튼이 곧 재시도다. **거절을 받으면 목록을 곧바로 다시 읽는다**(`onRefused`) —
 * 그 사이 누가 들어왔거나 기본이 됐다는 거절인데 다음 폴링까지 창은 지울 수 있다고 말한다. 다시 읽은 줄이 막혀 있으면
 * 막힘 문장이 그 거절을 말하므로 오류 줄은 거둔다 — 같은 막힘이 두 번 서지 않게.
 */
export function TierDeleteForm({
  tiers,
  onClose,
  onDone,
  onRefused,
}: {
  tiers: QuotaTier[]
  onClose: () => void
  onDone: () => void
  onRefused: () => void
}) {
  const [left, setLeft] = useState<string[]>(() => tiers.map((t) => t.id))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const targets = tiers.filter((t) => left.includes(t.id))
  const free = targets.filter((t) => tierBlocked(t) === null)
  const remove = async () => {
    setBusy(true)
    setError(null)
    let first: unknown = null
    const failed = new Set<string>()
    for (const t of free) {
      try {
        await api.remove(t.id)
      } catch (e) {
        if (first === null) first = e
        failed.add(t.id)
      }
    }
    setBusy(false)
    if (first !== null) {
      setError(first)
      // 막혀 있던 줄과 거절된 줄이 남는다 — 지워진 줄만 빠진다.
      setLeft((ids) => ids.filter((id) => failed.has(id) || !free.some((t) => t.id === id)))
      onRefused()
      return
    }
    onDone()
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>직위 지우기</DialogTitle>
      </DialogHeader>
      <ul aria-label="지울 직위" className="max-h-60 overflow-y-auto rounded-md border">
        {targets.map((t) => {
          const blocked = tierBlocked(t)
          return (
            <li key={t.id} className="grid gap-0.5 px-3 py-1.5 text-[13px]">
              <span className="truncate font-medium">{t.display_name}</span>
              {blocked ? <span className="text-[12px] text-warn-ink">{blocked}</span> : null}
            </li>
          )
        })}
      </ul>
      {free.length > 0 ? <p className="text-[13px]">직위가 목록에서 사라집니다.</p> : null}
      {/* 남은 것이 모두 막혀 있으면 거절은 그 막힘 문장이 말한다. */}
      <Err error={free.length > 0 ? error : null} />
      <DialogFooter>
        <Button variant="outline" disabled={busy} onClick={onClose}>
          {free.length > 0 ? '취소' : '닫기'}
        </Button>
        {free.length > 0 ? (
          <Button variant="destructive" disabled={busy} onClick={() => void remove()}>
            {busy ? '지우는 중' : '지우기'}
          </Button>
        ) : null}
      </DialogFooter>
    </>
  )
}
